import React, { useContext, useEffect, useState } from 'react'
import { CompositeFilterDescriptor, filterBy } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridFilterChangeEvent,
  GridPageChangeEvent,
} from '@progress/kendo-react-grid'
import { CreateIBProfileInstrument } from 'api/Trading-Accounts/Types/CreateIBProfileInstrument'
import { Modal } from 'components/CustomModal'
import { InputFilterCell } from 'components/Filter/FilterCell'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { normalizeFilter } from 'features/DataGrid/helpers'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchInstrumentsAction } from 'store/trading-accounts/trading-accounts.actions'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import stringComparator from 'utils/stringComparator'

import { useApiClient } from '../../../../../utils/api/ApiClient'
import { TradingAccountsApiClient } from '../../../../../utils/api/tradingAccountsApi'
import { IntroducingBrokerProfileInstrumentsAddInstrumentPage } from './IntroducingBrokerProfileInstrumentsAddInstrumentPage'
import { IntroducingBrokerProfileProps } from './types'

export const IntroducingBrokerProfileInstrumentsPage: React.FC<IntroducingBrokerProfileProps> = (
  props
) => {
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const { profile } = props
  const setToast = useContext(ToastContext)

  const [addInstrumentModal, setAddInstrumentModal] = useState(false)

  const { callback, forceRefresh } = useCallbackWithForceRefresh(
    () => partnershipsApiClient.getIntroducingBrokerProfileInstruments(profile.id),
    [profile.id]
  )
  const { data, isLoading } = useFetchOne(callback)

  const [filter, setFilter] = useState<CompositeFilterDescriptor>({ logic: 'and', filters: [] })
  const [paginationState, setPaginationState] = useState<{ skip: number; take: number }>(
    getPaginationInitialState()
  )

  const instruments = useAppSelector((state) => state.tradingAccounts.instruments)

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (isEmpty(instruments)) {
      dispatch(fetchInstrumentsAction(tradingAccountsApiClient))
    }
  }, [instruments]) // eslint-disable-line react-hooks/exhaustive-deps
  const handlePageChange = (event: GridPageChangeEvent) => {
    if (data && data.length >= 1) {
      setPaginationState({ skip: event.page.skip, take: event.page.take })
    }
  }

  const handleFilterChange = (event: GridFilterChangeEvent) => {
    setFilter(normalizeFilter(event.filter))
    setPaginationState(getPaginationInitialState())
  }

  const onSubmitAddInstruments = async (values: CreateIBProfileInstrument) => {
    await tradingAccountsApiClient.addIntroducingBrokerInstruments(profile.id, values)
    setAddInstrumentModal(false)
    setToast(successToast('Introducing Broker instruments added successfully.'))
    forceRefresh()
  }

  const gridData = filterBy(data || [], filter).sort((a, b) => stringComparator(a.symbol, b.symbol))

  return (
    <React.Fragment>
      {addInstrumentModal && (
        <Modal visible={!!addInstrumentModal} onClose={() => setAddInstrumentModal(false)}>
          <IntroducingBrokerProfileInstrumentsAddInstrumentPage
            // allInstruments={instruments || []}
            instruments={instruments || []}
            onSubmit={onSubmitAddInstruments}
          />
        </Modal>
      )}
      <Loading isLoading={isLoading}>
        <Grid
          resizable
          pageable
          filterable
          data={gridData.slice(paginationState.skip, paginationState.take + paginationState.skip)}
          filter={filter}
          skip={paginationState.skip}
          take={paginationState.take}
          total={gridData.length}
          onPageChange={handlePageChange}
          onFilterChange={handleFilterChange}
          data-test='table'
        >
          <Column field='symbol' title='Symbol' filterCell={InputFilterCell} />
          <Column field='name' title='Name' filterCell={InputFilterCell} />
        </Grid>
      </Loading>
    </React.Fragment>
  )
}
